import * as React from 'react';

import {IconButton} from '../button/icon-button/icon-button';
import {colors} from '../colors/colors';
import {Dialog} from '../dialog/dialog';
// @ts-expect-error - imported from .js/.jsx file
import {CogIcon} from '../icon/svg/cog-icon';
import {LockRegularIcon} from '../icon/svg/lock-regular-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TrashIcon} from '../icon/svg/trash-icon';
import {Stack} from '../layout/stack/stack';
import {Switch} from '../switch/switch';
import {Body} from '../typography/body';

import {getWidgetIconForType, getIsLockedWidget} from './helpers';
import type {RealAndFakeWidgetType} from './types';

import styles from './widget-list-item.module.css';

type Props = {
    name: string;
    hiddenProps?: {
        isHidden: boolean;
        onToggleHide: () => void;
    };
    editAndRemoveProps?: {
        onEdit: () => void;
        onRemove: () => void;
    };
    subtext?: string;
    type: RealAndFakeWidgetType;
    isDisabledByAdmin: boolean;
};

export function WidgetListItem(props: Props) {
    const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(false);
    const deleteButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const handleToggleHide = React.useCallback(() => {
        if (props.hiddenProps && props.hiddenProps.onToggleHide) {
            props.hiddenProps.onToggleHide();
        }
    }, [props.hiddenProps]);

    const Icon = getWidgetIconForType(props.type);
    const isLockedWidget = getIsLockedWidget(props.type);

    return (
        <>
            <div className={styles.content}>
                <Stack isHorizontal={true} alignItems='start' spacing={8}>
                    {isLockedWidget && <LockRegularIcon size={20} />}
                    {Icon && (
                        <Icon
                            size={20}
                            wrapWithDiv={false}
                            color={colors.grey}
                            fill={colors.grey}
                        />
                    )}
                    <Stack spacing={2}>
                        <Body isBold={true}>{props.name}</Body>
                        {props.subtext && <Body size='small'>{props.subtext}</Body>}
                    </Stack>
                </Stack>
                <Stack isHorizontal={true} alignItems='center' spacing={2}>
                    {props.isDisabledByAdmin && (
                        <LockRegularIcon size={20} data-tip='Disabled by admin' />
                    )}
                    {props.hiddenProps && !props.isDisabledByAdmin && (
                        <Switch
                            isOn={!props.hiddenProps.isHidden}
                            onChange={handleToggleHide}
                            isSmall={true}
                            isRounded={true}
                            shouldAnimate={false}
                            tooltipText={props.hiddenProps.isHidden ? 'Show widget' : 'Hide widget'}
                        />
                    )}
                    {props.editAndRemoveProps && (
                        <>
                            <IconButton
                                icon={CogIcon}
                                onClick={props.editAndRemoveProps.onEdit}
                                tooltipText='Edit'
                                isSmall={true}
                            />
                            <IconButton
                                icon={TrashIcon}
                                onClick={() => {
                                    setIsConfirmingDelete(true);
                                }}
                                tooltipText='Remove'
                                isSmall={true}
                                // @ts-expect-error
                                getButtonRef={(ref) => {
                                    deleteButtonRef.current = ref;
                                }}
                            />
                        </>
                    )}
                </Stack>
            </div>
            {isConfirmingDelete && (
                <Dialog
                    anchor={deleteButtonRef.current}
                    question='Are you sure you want to delete this widget?'
                    primaryText='Delete'
                    primaryVariant='danger'
                    secondaryText='Cancel'
                    onPrimary={() => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        props.editAndRemoveProps && props.editAndRemoveProps.onRemove();
                        setIsConfirmingDelete(false);
                    }}
                    onSecondary={() => {
                        setIsConfirmingDelete(false);
                    }}
                    customStyles={{
                        width: '220px',
                    }}
                />
            )}
        </>
    );
}
